import React, { useState, useEffect, useRef } from "react";
import styles from "../EditDiv/EditDiv.module.css";
import buttonStyles from "../YesNoButtonsCSS/YesNoButtons.module.css";
import BlueLogo from "../../assets/BlueLogo.png";
import { Grid, TextField, CircularProgress } from "@mui/material";
import { Form, FormikProvider } from "formik";
import { useFormik } from "formik";
import * as Yup from "yup";
import textfieldStyle from "../../GlobalStyles/styles";
import Dropdown from "../DropDownBox/Dropdown";
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from "../../redux/store";
import { submitStandardsAndTemplatesTicket, setSuccess, hasError } from "../../redux/slices/standardsAndTemplates";
import { useMsal } from "@azure/msal-react";

const StandardsAndTemplatesForm = ({ onSubmit, onClose, defaultEmail }) => {
  const initialValues = {
    natureOfTicket: "",
    urgency: "3",
    description: "",
    estimatedHours: "",
    meetingRequest: "",
    attachments: null,
    requesterEmail: "",
  };

  const dispatch = useDispatch();
  const standardsState = useSelector((state) => state.standardsAndTemplates || {});
  const [apiResponse, setApiResponse] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const { accounts } = useMsal();
  const account = accounts && accounts.length > 0 ? accounts[0] : null;
  const backdropRef = useRef(null);
  const formRef = useRef(null);

  // clear any previous success state when this form mounts so it won't auto-close
  React.useEffect(() => {
    dispatch(setSuccess(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflect redux state into local UI
  React.useEffect(() => {
    if (standardsState.successMessage) {
      const msg = standardsState.successMessage;
      enqueueSnackbar(msg, { variant: 'success' });
      // clear redux success so subsequent opens don't immediately close
      dispatch(setSuccess(null));
      // close form on success
      if (typeof onClose === 'function') onClose();
    }
    if (standardsState.error) {
      const errMsg = standardsState.error?.message || String(standardsState.error);
      enqueueSnackbar(errMsg, { variant: 'error' });
      // keep error in redux for debugging but clear it so UI won't be sticky
      dispatch(hasError(null));
    }
  }, [standardsState.successMessage, standardsState.error, enqueueSnackbar, onClose]);

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      natureOfTicket: Yup.string().required("Required"),
      urgency: Yup.string().required("Required"),
      description: Yup.string().required("Required"),
      estimatedHours: Yup.number().min(0, "Must be a positive number").required("Required"),
      meetingRequest: Yup.string().required("Required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("natureOfTicket", values.natureOfTicket || "");
      formData.append("urgency", values.urgency || "");
      formData.append("description", values.description || "");
      formData.append("estimatedHours", values.estimatedHours || "");
      formData.append("meetingRequest", values.meetingRequest || "");
      if (values.requesterEmail) formData.append("requesterEmail", values.requesterEmail);
      if (values.attachments && values.attachments.length) {
        Array.from(values.attachments).forEach((file, idx) => {
          formData.append(`file_${idx}`, file, file.name);
        });
      }

      try {
        await dispatch(submitStandardsAndTemplatesTicket(formData));
        // success handled by selector effect
      } catch (e) {
        // error handled by selector effect
      }
    },
  });

  // Set requesterEmail from MSAL username or provided defaultEmail
  useEffect(() => {
    const autoEmail = (typeof defaultEmail === 'string' && defaultEmail) || account?.username || '';
    if (autoEmail) formik.setFieldValue('requesterEmail', autoEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEmail, account]);

  // Ensure modal opens scrolled to the top and content is top-aligned
  useEffect(() => {
    if (backdropRef.current) backdropRef.current.scrollTop = 0;
    if (formRef.current) formRef.current.scrollTop = 0;
  }, []);

  const natureOfTicketOptions = [
    "AutoLisp Automations",
    "AutoCAD Templates (New Project/Layout Sheets)",
    "Blocks & Tool Palettes (Broken or New)",
    "Calculation Package",
    "Design Aids",
    "Design Handbook",
    "Document Handbook",
    "Drainage Documentation",
    "Drawing Rulebook",
    "Email Templates",
    "Exploratory Works Template",
    "Gold Standards",
    "ISA Documentation",
    "LTD",
    "PDF XChange",
    "RC Detailing",
    "ROO",
    "Scheming",
    "Standard Details",
    "Standard Notes & Schedules",
    "Structural Specification",
    "Sustainability Calculator",
    "Tekla Template/Guide",
    "Temporary Works Documentation",
    "Markup Policy",
  ];

  const handleFileChange = (e) => {
    formik.setFieldValue("attachments", e.target.files);
  };

  return (
    <div className={styles.backGround} ref={backdropRef}>
      <div
        className={styles.form}
        ref={formRef}
        style={{
          width: "600px",
          maxHeight: "80vh",
          overflowY: "auto",
          justifyContent: "flex-start",
          alignItems: "stretch",
          paddingTop: 12,
          paddingBottom: 8,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.momentTitle}>MOMENT</div>
        <div className={styles.sectionTitle}>Standards and Templates Request</div>

        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate style={{ width: "100%" }} onSubmit={formik.handleSubmit}>
            <Grid container sx={{ width: "100%" }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>1. What does the nature of your ticket consist of *</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <Dropdown
                  options={natureOfTicketOptions}
                  placeholder={"Select..."}
                  initialSelectedValue={formik.values.natureOfTicket}
                  onValueChange={(val) => formik.setFieldValue("natureOfTicket", val)}
                />
              </Grid>
            </Grid>

            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>2. Urgency *</p>
                <div style={{ fontSize: 12, color: "#666" }}>
                  4 complete within 2 days,<br />
                  3 complete within 1 week,<br />
                  2 complete within 2 weeks,<br />
                  1 complete within 3 weeks.
                </div>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <div style={{ display: "flex", gap: "8px" }}>
                  {[1, 2, 3, 4].map((n) => (
                    <div
                      key={n}
                      onClick={() => formik.setFieldValue("urgency", String(n))}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 4,
                        border: formik.values.urgency === String(n) ? "2px solid #3C96D4" : "1px solid #ddd",
                        cursor: "pointer",
                        background: formik.values.urgency === String(n) ? "#e9f5fb" : "#fff",
                      }}
                    >
                      {n}
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>

            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>3. Please provide a description of your query? *</p>
                <div style={{ fontSize: 12, color: "#666" }}>
                  If possible, please keep this concise
                </div>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={formik.values.description}
                  onChange={(e) => formik.setFieldValue("description", e.target.value)}
                  placeholder="Please give a short summary of what the task consists of"
                  sx={textfieldStyle}
                  error={Boolean(formik.touched.description && formik.errors.description)}
                />
              </Grid>
            </Grid>

            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>4. How long would you estimate the update work to take? (In hours) *</p>
                <div style={{ fontSize: 12, color: "#666" }}>
                  The value must be a number
                </div>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  type="number"
                  value={formik.values.estimatedHours}
                  onChange={(e) => formik.setFieldValue("estimatedHours", e.target.value)}
                  placeholder="Enter your answer"
                  sx={textfieldStyle}
                  error={Boolean(formik.touched.estimatedHours && formik.errors.estimatedHours)}
                />
              </Grid>
            </Grid>

            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>5. Would you like the person to book in a quick meeting with you so you can explain it in more detail? *</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <div style={{ display: "flex", gap: "16px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="meetingRequest"
                      value="Yes"
                      checked={formik.values.meetingRequest === "Yes"}
                      onChange={() => formik.setFieldValue("meetingRequest", "Yes")}
                    />
                    Yes
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="meetingRequest"
                      value="No"
                      checked={formik.values.meetingRequest === "No"}
                      onChange={() => formik.setFieldValue("meetingRequest", "No")}
                    />
                    No
                  </label>
                </div>
              </Grid>
            </Grid>

            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>6. Do you have any files to upload for the person to use/reference?</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <input type="file" multiple onChange={handleFileChange} />
              </Grid>
            </Grid>

            <div className={styles.updateAndCancel} style={{ marginTop: 16 }}>
              <div className={styles.updateButton} type="submit" onClick={formik.handleSubmit}>
                <div className={buttonStyles.YesButtonComponentStyling}>
                  {standardsState.isSubmitting ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'SUBMIT'}
                </div>
              </div>
              <div className={styles.cancelButton} onClick={onClose}>
                <div className={buttonStyles.NoButtonComponentStyling}>CANCEL</div>
              </div>
            </div>
          </Form>
        </FormikProvider>

        <img className={styles.blueLogo} src={BlueLogo} alt="BlueLogo" style={{ alignSelf: 'center' }} />
      </div>
    </div>
  );
};

export default StandardsAndTemplatesForm;